import { useEffect, useCallback, useMemo, useState } from 'react';

import { withStyles } from '@material-ui/core';

import { DataTable, CustomModal } from 'components/oldComponents';
import { useGetPaginatedSchoolsLazyQuery } from 'generated';
import { useBootstrapApp } from 'hooks';
import { formatNumberToBR, safeStorage } from 'utils';

import { defineColumns } from './config';
import { useStyles, styleSchoolTitle } from './styles';

const RenderSchoolTitle = withStyles(styleSchoolTitle)((props: any) => {
  const { classes, title, total, hasSearch } = props;

  return (
    <div className={classes.titleContainer}>
      <h3>{title}</h3>
      <span>
        Total de escolas participantes{hasSearch ? ' com este filtro:' : ':'}{' '}
        <b>{formatNumberToBR(total)}</b>
      </span>
    </div>
  );
});

interface EntitySchoolProps {
  openInitial: boolean;
  fnClose: (event: React.ChangeEvent) => void;
  onChangeEntity: (value: any) => void;
  searchStorageKey: string;
}

const limit = Number(process.env.REACT_APP_TABLE_PAGE_LIMIT);

export const EntitySchool: React.FC<EntitySchoolProps> = ({
  openInitial,
  fnClose,
  onChangeEntity,
  searchStorageKey,
}) => {
  const classes = useStyles();

  const { schools, entity, updateSchools, indicatorEventId } =
    useBootstrapApp();

  const [search, setSearch] = useState<Record<string, string>>(() => {
    const storageSearch = safeStorage.getItem(searchStorageKey);

    return storageSearch || {};
  });

  const [getPaginatedSchools, { loading, data, error }] =
    useGetPaginatedSchoolsLazyQuery({
      fetchPolicy: 'cache-first',
      variables: {
        limit,
        page: schools?._pagination.currentPage ?? 1,
        indicatorEventId,
      },
    });

  const memoRowsData = useMemo(() => {
    const schoolsList = schools?.data.map(school => ({
      ...school,
      brand: school?.brandNames.join(', ') || 'N/A',
    }));
    return schoolsList || [];
  }, [schools?.data]);

  const memoHasSearch = useMemo(() => {
    return !!Object.keys(search).length;
  }, [search]);

  const handlePagination = useCallback(
    (newPage: number) => {
      getPaginatedSchools({
        variables: {
          limit,
          page: newPage,
          searchName: search?.name,
          indicatorEventId,
        },
      });
    },
    [getPaginatedSchools, indicatorEventId, search?.name],
  );

  const handleSearchChange = useCallback(
    (searchParams: Record<string, string>) => {
      const hasSearch = !!Object.keys(searchParams).length;

      setSearch(searchParams);

      if (hasSearch) {
        safeStorage.setItem(searchStorageKey, searchParams);
        return;
      }

      safeStorage.removeItem(searchStorageKey);
    },
    [searchStorageKey],
  );

  const handleSearch = useCallback(
    (searchParams: Record<string, string>) => {
      if (Object.keys(searchParams)) {
        handleSearchChange(searchParams);
      }

      getPaginatedSchools({
        variables: {
          limit,
          page: 1,
          searchName: searchParams?.name,
          indicatorEventId,
        },
      });

      if (!Object.keys(searchParams)) {
        setTimeout(() => {
          handleSearchChange(searchParams);
        }, 1000);
      }
    },
    [getPaginatedSchools, handleSearchChange, indicatorEventId],
  );

  const handleClose = (event: React.ChangeEvent) => {
    event.preventDefault();
    fnClose(event);
    setSearch({});
  };

  const onChangeItemHeader = (item: any) => {
    onChangeEntity(item);
  };

  useEffect(() => {
    if (!!data && !loading && !error) {
      updateSchools(data?.schools);
    }
  }, [data, loading, error, updateSchools]);

  useEffect(() => {
    setSearch(() => {
      const storageSearch = safeStorage.getItem(searchStorageKey);

      return openInitial ? storageSearch || {} : {};
    });
  }, [searchStorageKey, openInitial]);

  return (
    <>
      <CustomModal
        height={700}
        contentTitle={
          <RenderSchoolTitle
            title="Localize a escola"
            total={schools?._pagination.totalCount}
            hasSearch={memoHasSearch}
          />
        }
        openValue={openInitial}
        fnClose={event => handleClose(event)}
      >
        <div className={classes.tableSchool}>
          <DataTable
            context={{
              handleClick: onChangeItemHeader,
              selectedItem: entity?.id,
            }}
            loading={loading}
            defineColumns={defineColumns}
            rowsData={memoRowsData}
            pagination={schools?._pagination}
            onPagination={handlePagination}
            onSearch={handleSearch}
            externalSearch={search}
            reloadTrigger={JSON.stringify(memoRowsData)}
            perPage={limit}
          />
        </div>
      </CustomModal>
    </>
  );
};

export default EntitySchool;
